import React from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { Link } from "gatsby"
import { motion } from "framer-motion"

export const buttonStyle = css`
  --border-color: var(--${props => (props.$primary ? "primary" : "secondary")});
  --text-color: #fff;
  --background: var(
    --gradient-${props => (props.$primary ? "primary" : "secondary")}
  );

  text-decoration: none;
  display: inline-flex;
  justify-content: center;
  flex-direction: row;
  height: 44px;
  gap: 20px;
  align-items: center;
  text-align: center;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  text-transform: uppercase;
  cursor: pointer;
  transition: filter 0.3s ease-in-out;
  padding: 15px 35px;
  box-sizing: border-box;
  color: #fff;
  border: 0;

  background: var(--brand-brown);
  width: ${props => (props.$fill ? "100%" : null)};

  ${props => {
    if (props.$inversed) {
      return `
  color: var(--brand-brown);
  border: 2px solid var(--brand-brown);
  background: none;
`
    } else {
      return `
  box-shadow: var(--shadow);
`
    }
  }}

  &:hover {
    filter: saturate(1.3);
  }

  &:disabled {
    opacity: 0.8;
    cursor: default;
  }
`

const StyledLink = styled(Link)`
  ${buttonStyle}
`

const StyledAnchor = styled.a`
  ${buttonStyle}
`

const StyledButton = styled.button`
  ${buttonStyle}
`

const IconWrapper = styled.div`
  margin-top: -1px;
`
const AnimatedDiv = styled(motion.div)`
  display: inline-block;
  width: ${props => (props.$fill ? "100%" : null)};
`
const Animation = ({ children, $fill }) => {
  return (
    <AnimatedDiv
      $fill={$fill}
      whileHover={{
        scale: 1.05,
      }}
      whileTap={{ scale: 0.95 }}
    >
      {children}
    </AnimatedDiv>
  )
}

const Button = props => {
  let icon = null
  if (props.icon) {
    icon = <IconWrapper>{props.icon}</IconWrapper>
  }

  if (props.submit) {
    return (
      <StyledButton
        type="submit"
        $fill={props.fill}
        style={props.style}
        direction={props.direction}
        $small={props.small}
        disabled={props.disabled}
      >
        {props.label}
        {icon}
      </StyledButton>
    )
  }

  if (props.to) {
    return (
      <Animation>
        <StyledLink
          to={props.to}
          $inversed={props.inversed}
          $fill={props.fill}
          $primary={props.primary}
          style={props.style}
        >
          {props.label}
          {icon}
        </StyledLink>
      </Animation>
    )
  }

  if (props.href) {
    return (
      <Animation>
        <StyledAnchor
          href={props.href}
          $inversed={props.inversed}
          $fill={props.fill}
          $primary={props.primary}
          style={props.style}
          target={props.external ? "_blank" : null}
          rel={props.external ? "noreferrer" : null}
        >
          {props.label}
          {icon}
        </StyledAnchor>
      </Animation>
    )
  }

  return (
    <Animation $fill={props.fill}>
      <StyledButton
        onClick={props.onClick}
        $inversed={props.inversed}
        $fill={props.fill}
        $primary={props.primary}
        style={props.style}
        type="button"
      >
        {props.label}
        {icon}
      </StyledButton>
    </Animation>
  )
}

Button.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  to: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.element,
  inversed: PropTypes.bool,
  fill: PropTypes.bool,
  primary: PropTypes.bool,
  disabled: PropTypes.bool,
}

Button.defaultProps = {
  fill: false,
  inversed: false,
  primary: false,
  disabled: false,
}

export { Button }
